import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import {
  TransferForm,
  type TransferFormData,
} from "Components/Transfer/TransferForm";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useIsTransferDisabled } from "Hooks/useIsTransferDisabled";
import { useTransferBetweenAccounts } from "Hooks/useTransferBetweenAccounts";
import { useUserMarketCurrency } from "Hooks/useUserMarketCurrency";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import { pickMainBankAccount } from "Utils/AccountUtils";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { Resources, useResource } from "Translations/Resources";

const ALLOWED_STATUSES = [
  BankAccountStatus.Active,
  BankAccountStatus.Restricted,
];

export const TransferComponent = () => {
  const { t } = useResource();
  const { data, isLoading } = useBankingAccounts();
  const { isTransferDisabled } = useIsTransferDisabled();
  const { transfer, isPending: isSubmitting } = useTransferBetweenAccounts();
  const marketCurrency = useUserMarketCurrency();

  if (isLoading) {
    return (
      <PrimaryPaper>
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      </PrimaryPaper>
    );
  }

  if (!data) {
    return (
      <PrimaryPaper>
        <ErrorText>{t(Resources.Transfer.NoBankAccountsError)}</ErrorText>
      </PrimaryPaper>
    );
  }

  const bankAccounts = data.items.filter(({ status }) =>
    ALLOWED_STATUSES.includes(status),
  );

  if (bankAccounts.length < 2) {
    return (
      <PrimaryPaper>
        <Typography>{t(Resources.Transfer.ConnectMoreAccounts)}</Typography>
      </PrimaryPaper>
    );
  }

  // Transfers are minted in the market currency (useTransferBetweenAccounts) — default
  // the source to the market rail's main account and prefer a same-currency counterpart
  // (a cross-currency pair would only die on the backend currency guard).
  const primaryAccount = pickMainBankAccount(bankAccounts, marketCurrency);
  const secondAccount =
    bankAccounts.find(
      (a) =>
        a.publicID !== primaryAccount?.publicID &&
        a.currency === primaryAccount?.currency,
    ) ?? bankAccounts.find((a) => a.publicID !== primaryAccount?.publicID);

  const defaultValues = {
    fromAccountPublicID: primaryAccount?.publicID,
    toAccountPublicID: secondAccount?.publicID,
  };

  const resolveAccount = (publicID: string) =>
    bankAccounts.find((a) => a.publicID === publicID);

  const handleSubmit = (formData: TransferFormData) => {
    if (isSubmitting) {
      return;
    }

    const fromAccount = resolveAccount(formData.fromAccountPublicID);
    const toAccount = resolveAccount(formData.toAccountPublicID);

    if (!fromAccount || !toAccount) {
      return;
    }

    transfer({
      amount: formData.amount,
      senderBankAccountID: fromAccount.bankAccountID,
      senderEnvironment: fromAccount.environment,
      receiverBankAccountID: toAccount.bankAccountID,
      note: formData.note || null,
    });
  };

  return (
    <PrimaryPaper>
      <TransferForm
        bankAccounts={bankAccounts}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isDisabled={isTransferDisabled}
      />
    </PrimaryPaper>
  );
};
